import React, { useEffect } from "react";
import { types } from "@authzsvc/api-lib";
import { Card, Alert, Typography, Table, Spin } from "antd";
import {
  LinkOutlined,
  ApiOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import { getGroupRoleAssignments } from "../../actions/api";
import { connect } from "react-redux";
import { isApiCallLoading } from "../../selectors";
import PropTypes from "prop-types";
import { sidebar_applications } from "../../config";
import { useParams } from "react-router-dom";

const getAppPortalUrl = (appId) => `${sidebar_applications}/manage/${appId}`;

const getRoleInAppPortalUrl = (appId, roleId) =>
  `${sidebar_applications}/manage/${appId}/role/${roleId}`;

export const GroupRoles = ({
  loading,
  getGroupRoleAssignments,
  roleAssignments,
}) => {
  const { id } = useParams();
  useEffect(() => {
    getGroupRoleAssignments(id);
  }, [getGroupRoleAssignments, id]);

  const columns = [
    {
      title: "Application",
      dataIndex: "applicationIdentifier",
      key: "application",
      render: (text, record) => (
        <Typography.Link
          href={getAppPortalUrl(record.applicationId)}
          target="_blank"
          rel="noopener noreferrer"
        >
          <LinkOutlined aria-hidden="true" /> {record.applicationIdentifier}
        </Typography.Link>
      ),
    },
    {
      title: "Roles",
      dataIndex: "roles",
      key: "roles",
      render: (roles) => (
        <ul style={{ listStyleType: "none", margin: 0, padding: 0 }}>
          {roles.map((r) => (
            <li key={r.id} style={{ padding: "0.3em 0" }}>
              <Typography.Link
                href={getRoleInAppPortalUrl(r.applicationId, r.id)}
                target="_blank"
                rel="noopener noreferrer"
              >
                <LinkOutlined aria-hidden="true" /> {r.name}
              </Typography.Link>{" "}
              - {r.description}
            </li>
          ))}
        </ul>
      ),
    },
  ];

  return (
    <Card
      title={
        <Typography.Title level={4} style={{ margin: 0 }}>
          <ApiOutlined
            style={{ marginRight: 8, color: "#bfbfbf" }}
            aria-hidden="true"
          />
          Group role assignments
        </Typography.Title>
      }
    >
      <Spin spinning={loading}>
        {roleAssignments.length === 0 && (
          <Alert
            type="warning"
            showIcon
            icon={
              <ExclamationCircleOutlined
                style={{ marginRight: 16 }}
                aria-hidden="true"
              />
            }
            message={
              <>
                <Typography.Title level={5} style={{ margin: 0 }}>
                  No role assignments found!
                </Typography.Title>
                This group is either not linked to any application via roles or
                you are not allowed to see the assignments.
              </>
            }
          />
        )}
        {roleAssignments.length > 0 && (
          <>
            <Alert
              type="info"
              showIcon
              icon={
                <InfoCircleOutlined
                  style={{ marginRight: 16 }}
                  aria-hidden="true"
                />
              }
              aria-label="Information"
              message={
                <>
                  Membership to this group grants you the following roles in the
                  applications listed below.
                  <br />
                  Clicking on the name of the application or the role will open
                  a new tab with the application details. You may not have
                  permissions to read these details on the other portal.
                </>
              }
              style={{ marginBottom: 16 }}
            />
            <Table
              columns={columns}
              dataSource={roleAssignments}
              rowKey="id"
              pagination={false}
              bordered
              aria-label="Group role assignments table"
            />
          </>
        )}
      </Spin>
    </Card>
  );
};

GroupRoles.propTypes = {
  loading: PropTypes.bool.isRequired,
  roleAssignments: PropTypes.array.isRequired,
  getGroupRoleAssignments: PropTypes.func.isRequired,
};

export default connect(
  (state) => ({
    roleAssignments: state.groups.groupRoleAssignments,
    loading: isApiCallLoading(state, types.GET_GROUP_ROLE_ASSIGNMENTS),
  }),
  {
    getGroupRoleAssignments,
  }
)(GroupRoles);
